import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, BookOpen, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { AudioButton } from '../components/AudioButton';
import { CourseQuiz } from '../components/CourseQuiz';
import { ExerciseSection } from '../components/ExerciseSection';
import { toast } from 'sonner';
import { useCourseContent } from '../hooks/useContentData';
import { GrammatikSection } from '../components/GrammatikSection';
import { A2_2_ANSWERS, A2_2_SECTIONS } from '../data/quizBank';
import EXERCISES_A22 from '../data/exerciseBankA22';

export default function MenschenA22() {
  const [activeTab, setActiveTab] = useState('l13');
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [bookUrl, setBookUrl] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState('kursbuch');
  const { lessons, grammatik: grammatikA22, loading } = useCourseContent('menschen_a22');
  const API = process.env.REACT_APP_BACKEND_URL;

  const BOOKS_A22 = {
    kursbuch: { id: 'menschen_a22_kursbuch', label: 'Kursbuch', labelEn: 'Textbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/b37275c64877255145587b16b80d37f4/edureader/app/pdf/lib/web/viewer.html' },
    arbeitsbuch: { id: 'menschen_a22_arbeitsbuch', label: 'Arbeitsbuch', labelEn: 'Workbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/0408783c0597a306985422f8fb34b111/edureader/app/pdf/lib/web/viewer.html' },
  };

  const fetchBookUrl = async (bookType) => {
    const book = BOOKS_A22[bookType || selectedBook];
    setBookLoading(true);
    setBookUrl(null);
    try {
      const res = await fetch(`${API}/api/books/${book.id}/url`);
      const data = await res.json();
      setBookUrl(data.viewer_url || book.url);
    } catch { setBookUrl(book.url); }
    finally { setBookLoading(false); }
  };

  const tabs = [
    { id: 'mod5', label: 'Modul 5', icon: '📘', isHeader: true },
    { id: 'l13', label: 'L13: Schule', icon: '🏫' },
    { id: 'l14', label: 'L14: Gesellschaft', icon: '👥' },
    { id: 'l15', label: 'L15: Events', icon: '🎭' },
    { id: 'g5', label: 'Grammatik 13-15', icon: '📐', isGrammatik: true },
    { id: 'mod6', label: 'Modul 6', icon: '📗', isHeader: true },
    { id: 'l16', label: 'L16: Umwelt', icon: '🌿' },
    { id: 'l17', label: 'L17: Beruf', icon: '💼' },
    { id: 'l18', label: 'L18: Gesundheit', icon: '🏥' },
    { id: 'g6', label: 'Grammatik 16-18', icon: '📐', isGrammatik: true },
    { id: 'mod7', label: 'Modul 7', icon: '📙', isHeader: true },
    { id: 'l19', label: 'L19: Erlebnisse', icon: '✨' },
    { id: 'l20', label: 'L20: Regeln', icon: '📋' },
    { id: 'l21', label: 'L21: Leben', icon: '🌍' },
    { id: 'g7', label: 'Grammatik 19-21', icon: '📐', isGrammatik: true },
    { id: 'mod8', label: 'Modul 8', icon: '📕', isHeader: true },
    { id: 'l22', label: 'L22: Nachrichten', icon: '📰' },
    { id: 'l23', label: 'L23: Arbeit', icon: '🏢' },
    { id: 'l24', label: 'L24: Rückblick', icon: '🎓' },
    { id: 'g8', label: 'Grammatik 22-24', icon: '📐', isGrammatik: true },
    { id: 'pdf', label: 'Interaktives Lernen', icon: '📖', isPdf: true },
    { id: 'exercises', label: 'Übungen', icon: '🏋️', isExercise: true },
    { id: 'quiz', label: 'Quiz', icon: '✅' },
  ];

  const lessonMap = {
    l13: lessons['lektion13'], l14: lessons['lektion14'], l15: lessons['lektion15'],
    l16: lessons['lektion16'], l17: lessons['lektion17'], l18: lessons['lektion18'],
    l19: lessons['lektion19'], l20: lessons['lektion20'], l21: lessons['lektion21'],
    l22: lessons['lektion22'], l23: lessons['lektion23'], l24: lessons['lektion24'],
  };

  const courseContents = {
    l13: { grammar: 'Relativs\u00e4tze', topics: ['School memories', 'Describing people', 'Education system'], grammarAr: '\u0627\u0644\u062c\u0645\u0644 \u0627\u0644\u0645\u0648\u0635\u0648\u0644\u0629' },
    l14: { grammar: 'Passiv Pr\u00e4sens', topics: ['Social changes', 'Statistics', 'Digitalization'], grammarAr: '\u0627\u0644\u0645\u0628\u0646\u064a \u0644\u0644\u0645\u062c\u0647\u0648\u0644' },
    l15: { grammar: 'obwohl / trotzdem / deshalb', topics: ['Events & culture', 'Contradictions', 'Recommendations'], grammarAr: '\u0631\u063a\u0645 / \u0645\u0639 \u0630\u0644\u0643 / \u0644\u0630\u0644\u0643' },
    l16: { grammar: 'um...zu / damit', topics: ['Environment', 'Waste separation', 'Climate change'], grammarAr: '\u0644\u0643\u064a / \u062d\u062a\u0649' },
    l17: { grammar: 'Infinitiv mit zu', topics: ['Job applications', 'CV & interview', 'Work conditions'], grammarAr: '\u0627\u0644\u0645\u0635\u062f\u0631 \u0645\u0639 zu' },
    l18: { grammar: 'sollte + wenn', topics: ['Health advice', 'Doctor visits', 'Sick leave'], grammarAr: '\u0627\u0644\u0646\u0635\u0627\u0626\u062d \u0627\u0644\u0635\u062d\u064a\u0629' },
    l19: { grammar: 'bevor / nachdem / w\u00e4hrend', topics: ['First experiences', 'Before & after', 'Telling anecdotes'], grammarAr: '\u0642\u0628\u0644 / \u0628\u0639\u062f / \u0623\u062b\u0646\u0627\u0621' },
    l20: { grammar: 'd\u00fcrfen / m\u00fcssen / sollen', topics: ['German rules', 'Quiet hours', 'Cultural norms'], grammarAr: '\u0627\u0644\u0642\u0648\u0627\u0639\u062f \u0648\u0627\u0644\u0642\u0648\u0627\u0646\u064a\u0646' },
    l21: { grammar: 'Genitiv', topics: ['Life stories', 'Biographies', 'Emigration'], grammarAr: '\u0627\u0644\u0645\u0636\u0627\u0641 \u0625\u0644\u064a\u0647' },
    l22: { grammar: 'Passiv Pr\u00e4teritum', topics: ['News reports', 'Accidents & rescue', 'Indirect speech'], grammarAr: '\u0627\u0644\u0645\u0628\u0646\u064a \u0644\u0644\u0645\u062c\u0647\u0648\u0644 \u0645\u0627\u0636\u064a' },
    l23: { grammar: 'sowohl...als auch / weder...noch', topics: ['Office life', 'Formal emails', 'Giving feedback'], grammarAr: '\u0643\u0644\u0627\u0647\u0645\u0627 / \u0644\u0627 \u0647\u0630\u0627 \u0648\u0644\u0627 \u0630\u0627\u0643' },
    l24: { grammar: 'A2 Zusammenfassung', topics: ['Review all grammar', 'Learning strategies', 'Motivation for B1'], grammarAr: '\u0645\u0644\u062e\u0635 A2' },
  };

  const exerciseKeys = ['L13','L14','L15','L16','L17','L18','L19','L20','L21','L22','L23','L24'];
  const exerciseLabels = ['L13: Schule','L14: Gesellschaft','L15: Events','L16: Umwelt','L17: Beruf','L18: Gesundheit','L19: Erlebnisse','L20: Regeln','L21: Leben','L22: Nachrichten','L23: Arbeit','L24: Rückblick'];

  const handleTranscription = (text) => { if (text) toast.info(`You said: "${text}"`); };
  const PracticeButton = ({ text }) => (
    <div className="inline-flex items-center gap-1">
      <AudioButton text={text} size="sm" voice="nova" />
      <MicrophoneButton size="sm" expectedText={text} onTranscription={handleTranscription} onPronunciationScore={() => {}} />
    </div>
  );

  const renderModuleTab = (tabId, moduleData) => {
    const cc = courseContents[tabId];
    return (
    <div className={`section ${activeTab === tabId ? 'active' : ''}`} data-testid={`tab-${tabId}`}>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>{moduleData.title.de}</h2>
      <p style={{ color: '#94a3b8', marginBottom: '12px' }}>{moduleData.title.en} · <span className="ar">{moduleData.title.ar}</span></p>

      {cc && (
        <div style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(139,92,246,0.03))', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }} data-testid={`course-contents-${tabId}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#a78bfa' }}>Kursinhalt / Course Contents</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}>
              Grammatik: {cc.grammar}
            </span>
            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)', direction: 'rtl' }}>
              {cc.grammarAr}
            </span>
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8' }}>
            {cc.topics.map((t, i) => <li key={`t-${tabId}-${i}`}>{t}</li>)}
          </ul>
        </div>
      )}

      {moduleData.sections.map((section, si) => {
        const sKey = `${tabId}-${section.type}-${si}`;
        if (section.type === 'vocab_table') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body">
                <table className="vocab-table">
                  <thead><tr><th>Deutsch</th><th>English</th><th className="ar">عربي</th>{section.hasAudio && <th>Üben</th>}</tr></thead>
                  <tbody>
                    {section.items.map((item, i) => (
                      <tr key={`r-${i}-${item.de?.slice(0,8)}`}>
                        <td className="de">{item.de}</td><td className="en">{item.en}</td><td className="ar">{item.ar}</td>
                        {section.hasAudio && <td><PracticeButton text={item.de} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (section.type === 'grammar_table') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body"><div className="grammar-box"><table className="grammar-table">
                <thead><tr>{section.headers.map((h, hi) => <th key={`h-${h}-${hi}`} className={h === 'عربي' ? 'ar' : ''}>{h}</th>)}</tr></thead>
                <tbody>{section.rows.map((row, ri) => (<tr key={`row-${ri}-${row[0]}`}>{row.map((cell, ci) => (<td key={`c-${ri}-${ci}`} className={ci === 1 ? 'em' : ci === row.length - 1 ? 'ar' : ''}>{cell}</td>))}</tr>))}</tbody>
              </table></div></div>
            </div>
          );
        }
        if (section.type === 'phrase_grid') {
          return (
            <div key={sKey} className="card">
              <div className="card-header"><span className="card-icon">{section.icon}</span><h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3></div>
              <div className="card-body"><div className="phrase-grid">
                {section.items.map((item, i) => (
                  <div key={`p-${item.ctx}-${i}`} className="phrase-card">
                    <div className="phrase-ctx">{item.ctx}</div>
                    <div className="phrase-de flex items-center justify-between"><span>{item.de}</span><PracticeButton text={item.de} /></div>
                    <div className="phrase-en">{item.en}</div><div className="phrase-ar">{item.ar}</div>
                  </div>
                ))}
              </div></div>
            </div>
          );
        }
        if (section.type === 'dialog') {
          return (
            <div key={sKey} className="dialog-box">
              <div className="dialog-title">{section.title}</div>
              <div className="dialog-content">
                {section.lines.map((line, j) => (
                  <div key={`dl-${line.speaker}-${j}`} className="dialog-line">
                    <div className="dialog-speaker" style={{ color: j % 2 === 0 ? '#8b5cf6' : '#f59e0b' }}>{line.speaker}:</div>
                    <div className={`dialog-bubble ${j % 2 !== 0 ? 'patient' : ''}`}>
                      <div className="de" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>{line.de}</span><PracticeButton text={line.de} /></div>
                      <div className="en">{line.en}</div><div className="ar">{line.ar}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (section.type === 'tip') {
          return (
            <div key={sKey} className="tip-box" style={{ marginTop: '16px' }}>
              <strong>Tipp · Tip · <span className="ar">نصيحة</span></strong><br />
              {section.text.de} · {section.text.en} · <span className="ar">{section.text.ar}</span>
            </div>
          );
        }
        return null;
      })}
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="menschen-a22-page">
      <style>{`
        .ma22-container { max-width: 900px; margin: 0 auto; padding: 20px; }
        .ma22-header { text-align: center; padding: 30px 0; border-bottom: 1px solid #334155; margin-bottom: 20px; }
        .ma22-header h1 { font-size: 28px; font-weight: 700; color: #8b5cf6; margin-bottom: 8px; }
        .ma22-header p { color: #94a3b8; font-size: 14px; }
        .ma22-header .modul-badges { display: flex; justify-content: center; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
        .ma22-header .modul-badge { padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .tab-nav { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; justify-content: center; }
        .tab-btn { padding: 10px 16px; border-radius: 8px; background: #1e293b; border: 1px solid #334155; color: #94a3b8; cursor: pointer; transition: all 0.2s; font-size: 13px; }
        .tab-btn:hover { background: #334155; }
        .tab-btn.active { background: #8b5cf6; color: white; border-color: #8b5cf6; }
        .tab-btn.module-header { background: transparent; border: none; color: #a78bfa; font-weight: 700; cursor: default; font-size: 13px; padding: 10px 6px; }
        .tab-btn.pdf-tab { border-color: rgba(245,158,11,0.4); color: #f59e0b; }
        .tab-btn.pdf-tab.active { background: #f59e0b; color: white; }
        .tab-btn.exercise-tab { border-color: rgba(20,184,166,0.4); color: #14b8a6; }
        .tab-btn.exercise-tab.active { background: #14b8a6; color: white; }
        .section { display: none; } .section.active { display: block; }
        .card { background: #1e293b; border: 1px solid #334155; border-radius: 12px; margin-bottom: 20px; overflow: hidden; }
        .card-header { padding: 16px 20px; border-bottom: 1px solid #334155; display: flex; align-items: center; gap: 12px; }
        .card-icon { font-size: 24px; }
        .card-header h3 { font-size: 16px; font-weight: 600; color: #e2e8f0; }
        .card-body { padding: 20px; }
        .grammar-tag { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; background: rgba(139,92,246,0.15); color: #a78bfa; border: 1px solid rgba(139,92,246,0.3); margin-top: 8px; }
        .vocab-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .vocab-table th { background: #0f172a; padding: 12px; text-align: left; font-weight: 600; color: #94a3b8; }
        .vocab-table td { padding: 12px; border-bottom: 1px solid #334155; }
        .vocab-table .de { color: #f1f5f9; font-weight: 500; }
        .vocab-table .en { color: #94a3b8; }
        .vocab-table .ar { color: #8b5cf6; direction: rtl; text-align: right; }
        .phrase-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
        .phrase-card { background: #0f172a; border-radius: 8px; padding: 12px; }
        .phrase-ctx { font-size: 11px; color: #8b5cf6; text-transform: uppercase; margin-bottom: 4px; }
        .phrase-de { font-size: 15px; font-weight: 600; color: #f1f5f9; }
        .phrase-en { font-size: 13px; color: #94a3b8; }
        .phrase-ar { font-size: 13px; color: #8b5cf6; direction: rtl; }
        .grammar-box { background: #0f172a; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
        .grammar-table { width: 100%; font-size: 13px; }
        .grammar-table th { background: #1e293b; padding: 10px; text-align: left; color: #94a3b8; }
        .grammar-table td { padding: 10px; border-bottom: 1px solid #334155; }
        .grammar-table .em { color: #f59e0b; font-weight: 600; }
        .grammar-table .ar { color: #8b5cf6; direction: rtl; }
        .dialog-box { background: #1e293b; border: 1px solid #334155; border-radius: 12px; margin-bottom: 16px; overflow: hidden; }
        .dialog-title { background: #0f172a; padding: 12px 16px; font-weight: 600; font-size: 14px; color: #8b5cf6; }
        .dialog-content { padding: 16px; }
        .dialog-line { display: flex; gap: 12px; margin-bottom: 12px; }
        .dialog-speaker { min-width: 80px; font-weight: 600; font-size: 12px; padding-top: 4px; }
        .dialog-bubble { flex: 1; background: #0f172a; border-radius: 8px; padding: 10px 14px; }
        .dialog-bubble.patient { background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3); }
        .dialog-bubble .de { font-weight: 500; color: #f1f5f9; margin-bottom: 4px; }
        .dialog-bubble .en { font-size: 12px; color: #94a3b8; }
        .dialog-bubble .ar { font-size: 12px; color: #8b5cf6; direction: rtl; }
        .tip-box { background: linear-gradient(135deg, rgba(139,92,246,0.1), rgba(139,92,246,0.05)); border: 1px solid rgba(139,92,246,0.3); border-radius: 8px; padding: 14px 16px; margin-top: 16px; }
        .tip-box strong { color: #8b5cf6; }
        .tab-btn.grammatik-tab { border-color: rgba(167,139,250,0.4); color: #a78bfa; }
        .tab-btn.grammatik-tab.active { background: #a78bfa; color: white; }
        .pdf-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: linear-gradient(135deg, #1e293b, #0f172a); border: 1px solid rgba(245,158,11,0.3); border-radius: 12px 12px 0 0; }
        .pdf-header-left { display: flex; align-items: center; gap: 12px; }
        .pdf-actions { display: flex; gap: 8px; }
        .pdf-action-btn { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background: rgba(245,158,11,0.15); color: #f59e0b; border: 1px solid rgba(245,158,11,0.3); cursor: pointer; transition: all 0.2s; text-decoration: none; }
        .pdf-iframe-container { border: 1px solid rgba(245,158,11,0.2); border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; background: #0f172a; }
        .pdf-iframe { width: 100%; height: 75vh; border: none; display: block; }
        .pdf-fullscreen { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999; background: #0f172a; }
        .pdf-fullscreen .pdf-header { border-radius: 0; }
        .pdf-fullscreen .pdf-iframe-container { border-radius: 0; border: none; }
        .pdf-fullscreen .pdf-iframe { height: calc(100vh - 68px); }
        .pdf-fallback { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 40px 20px; background: linear-gradient(135deg, #1e293b, #0f172a); border-top: 1px solid rgba(245,158,11,0.1); }
        .pdf-open-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 28px; background: #f59e0b; color: #0f172a; font-weight: 600; font-size: 15px; border-radius: 10px; cursor: pointer; border: none; transition: all 0.2s; }
        .pdf-open-btn:hover { background: #fbbf24; transform: translateY(-1px); }
        @media (max-width: 640px) { .ma22-container { padding: 12px; } .tab-btn { padding: 8px 12px; font-size: 12px; } }
      `}</style>

      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-link">
            <ChevronLeft className="h-5 w-5" /><span className="text-sm">Back</span>
          </Link>
          <h1 className="text-violet-500 font-bold">Menschen A2.2</h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="ma22-container">
        <div className="ma22-header">
          <h1>Menschen A2.2</h1>
          <p>German for Pre-Intermediate – Part 2 · <span style={{ color: '#8b5cf6' }}>الألمانية للمتوسط المبتدئ – الجزء الثاني</span></p>
          <div className="modul-badges">
            <span className="modul-badge" style={{ background: 'rgba(139,92,246,0.2)', color: '#a78bfa' }}>Modul 5–8</span>
            <span className="modul-badge" style={{ background: 'rgba(167,139,250,0.2)', color: '#c4b5fd' }}>12 Lektionen</span>
            <span className="modul-badge" style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>A2.2 Level</span>
          </div>
        </div>

        <div className="tab-nav" data-testid="tab-navigation">
          {tabs.map(tab => {
            if (tab.isHeader) return <span key={tab.id} className="tab-btn module-header">{tab.icon} {tab.label}</span>;
            return (
              <button key={tab.id} data-testid={`tab-btn-${tab.id}`}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${tab.isPdf ? 'pdf-tab' : ''} ${tab.isExercise ? 'exercise-tab' : ''} ${tab.isGrammatik ? 'grammatik-tab' : ''}`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.icon} {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
        ) : (
          <>
            {Object.entries(lessonMap).filter(([, data]) => data).map(([tabId, data]) => (
              <React.Fragment key={tabId}>{renderModuleTab(tabId, data)}</React.Fragment>
            ))}

            {['g5','g6','g7','g8'].map((gid, gi) => {
              const keys = [['l13','l14','l15'],['l16','l17','l18'],['l19','l20','l21'],['l22','l23','l24']][gi];
              return (
                <div key={gid} className={`section ${activeTab === gid ? 'active' : ''}`} data-testid={`tab-${gid}`}>
                  <GrammatikSection items={keys.map(k => grammatikA22[k]).filter(Boolean)} accentColor="#8b5cf6" />
                </div>
              );
            })}
          </>
        )}

        <div className={`section ${activeTab === 'quiz' ? 'active' : ''}`} data-testid="tab-quiz">
          <CourseQuiz quizId="menschen_a22_quiz" courseId="menschen_a22" title="Menschen A2.2 – Abschlussquiz" sections={A2_2_SECTIONS} answers={A2_2_ANSWERS} isActive={true} />
        </div>

        {activeTab === 'exercises' && (
          <div className="section active" data-testid="tab-exercises">
            <ExerciseLessonPicker exercises={EXERCISES_A22} exerciseKeys={exerciseKeys} labels={exerciseLabels} />
          </div>
        )}

        <div className={`section ${activeTab === 'pdf' ? 'active' : ''}`} data-testid="tab-pdf">
          {activeTab === 'pdf' && (
            <div className={pdfFullscreen ? 'pdf-fullscreen' : ''}>
              <div className="pdf-header">
                <div className="pdf-header-left">
                  <BookOpen className="h-5 w-5" style={{ color: '#f59e0b' }} />
                  <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Interaktives Lernen – Menschen A2.2</h2>
                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: '2px 0 0' }}>Interactive Textbook · الكتاب التفاعلي</p>
                  </div>
                </div>
                <div className="pdf-actions">
                  {bookUrl && (
                    <>
                      <button className="pdf-action-btn" onClick={() => setPdfFullscreen(!pdfFullscreen)} data-testid="pdf-fullscreen-toggle">
                        {pdfFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                      </button>
                      <a href={bookUrl} target="_blank" rel="noopener noreferrer" className="pdf-action-btn" data-testid="pdf-new-tab">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', background: '#0f172a', borderLeft: '1px solid rgba(245,158,11,0.2)', borderRight: '1px solid rgba(245,158,11,0.2)' }}>
                {Object.entries(BOOKS_A22).map(([key, book]) => (
                  <button key={key} onClick={() => { setSelectedBook(key); setBookUrl(null); }}
                    style={{ padding: '8px 20px', borderRadius: '8px', border: selectedBook === key ? '2px solid #f59e0b' : '1px solid #334155', background: selectedBook === key ? 'rgba(245,158,11,0.15)' : '#1e293b', color: selectedBook === key ? '#f59e0b' : '#94a3b8', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                    data-testid={`book-select-${key}`}>
                    {key === 'kursbuch' ? '📕' : '📗'} {book.label} ({book.labelEn})
                  </button>
                ))}
              </div>
              {!bookUrl && !bookLoading && (
                <div className="pdf-fallback">
                  <BookOpen className="h-12 w-12" style={{ color: '#f59e0b', marginBottom: '16px' }} />
                  <p style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '17px', marginBottom: '8px' }}>{BOOKS_A22[selectedBook].label} – Menschen A2.2</p>
                  <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>Klicken Sie, um das {BOOKS_A22[selectedBook].label} zu laden.</p>
                  <button onClick={() => fetchBookUrl(selectedBook)} className="pdf-open-btn" data-testid="pdf-load-book">
                    <BookOpen className="h-4 w-4" /> {BOOKS_A22[selectedBook].label} laden
                  </button>
                </div>
              )}
              {bookLoading && (
                <div className="pdf-fallback">
                  <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#f59e0b', marginBottom: '16px' }} />
                  <p style={{ color: '#e2e8f0', fontWeight: 600 }}>Buch wird geladen...</p>
                </div>
              )}
              {bookUrl && (
                <div className="pdf-iframe-container">
                  <iframe src={bookUrl} title={`Menschen A2.2 ${BOOKS_A22[selectedBook].label}`} className="pdf-iframe" allow="fullscreen" data-testid="pdf-iframe" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExerciseLessonPicker({ exercises, exerciseKeys, labels }) {
  const [selectedLesson, setSelectedLesson] = useState(exerciseKeys[0]);
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {exerciseKeys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-violet-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} exercises={exercises} />
    </div>
  );
}
